import React, { useEffect, useState } from "react";
import { Plus, Wallet, ImageIcon, Video, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export function AddCreditsButton({ onDone, size = "sm", className = "" }) {
  const [open, setOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [qty, setQty] = useState({ image: 5, video: 1 });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { if (open) api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {}); }, [open]);
  const catalog = (wallet?.catalog || [{ kind: "image", label: "صورة", price: 7, group: "image" }, { kind: "video", label: "فيديو", price: 12, group: "video" }]).filter((it) => it.price > 0);
  const cost = catalog.reduce((t, it) => t + (Number(qty[it.kind]) || 0) * it.price, 0);
  const count = catalog.reduce((t, it) => t + (Number(qty[it.kind]) || 0), 0);

  const buy = async () => {
    setBusy(true);
    try {
      const items = Object.fromEntries(catalog.filter((it) => Number(qty[it.kind]) > 0).map((it) => [it.kind, Number(qty[it.kind])]));
      const { data } = await api.post("/credits/buy", { items });
      toast.success(`تمت إضافة الرصيد: ${data.images_left} صورة/خدمة · ${data.videos_left} فيديو — المتبقي بالمحفظة ${data.balance} ر.س`);
      setOpen(false); onDone?.(data);
      window.dispatchEvent(new Event("rawaj:usage-changed"));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant="outline" className={`gap-1.5 border-accent/40 text-accent hover:bg-accent/10 ${className}`} onClick={() => setOpen(true)} data-testid="add-credits-btn">
        <Plus className="w-4 h-4" /> إضافة رصيد
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="add-credits-dialog">
          <DialogHeader><DialogTitle>اشترِ خدمات وأضفها إلى رصيدك</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">حدد الكمية من كل خدمة (من 8 ريال للغلاف حتى 29 لبطاقة العمل) — يُخصم المجموع من المحفظة ويُضاف لرصيدك الجاهز للتوليد الفردي أو الحملات.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[50vh] overflow-y-auto pr-1">
            {catalog.map((it) => (
              <label key={it.kind} className="rounded-xl border border-border bg-secondary/40 p-2.5 block" data-testid={`credits-item-${it.kind}`}>
                <span className="text-[11px] flex items-center justify-between gap-1 mb-1">
                  <span className="flex items-center gap-1 font-bold">{it.group === "video" ? <Video className="w-3.5 h-3.5 text-primary" /> : <ImageIcon className="w-3.5 h-3.5 text-primary" />} {it.label}</span>
                  <span className="text-primary font-bold whitespace-nowrap">{it.price} ر.س</span>
                </span>
                <Input type="number" min={0} value={qty[it.kind] ?? 0} onChange={(e) => setQty((q) => ({ ...q, [it.kind]: e.target.value }))} className="bg-background h-8" data-testid={`credits-${it.kind}-input`} />
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1 text-muted-foreground"><Wallet className="w-4 h-4" /> المحفظة: <b data-testid="credits-wallet-balance">{wallet?.balance ?? "…"} ر.س</b></span>
            <span>{count} خدمة · التكلفة: <b className="text-primary" data-testid="credits-cost">{cost.toFixed(2)} ر.س</b></span>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setOpen(false); navigate("/app/packages"); }} data-testid="credits-topup-link">شحن المحفظة</Button>
            <Button size="sm" disabled={busy || cost <= 0} onClick={buy} className="bg-primary text-white gap-1" data-testid="credits-buy-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} تأكيد الإضافة
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
